import { CROP_EPSILON, CameraZoomEffect, getSoftwareZoom, setSoftwareZoom } from '../../stream-effects/camera-zoom';
import { isMobileBrowser } from '../environment/utils';
import { getLocalDesktopTrack, getLocalTracks } from '../tracks/functions';

import { CAMERA_ZOOM_MAX, CAMERA_ZOOM_MIN, MEDIA_TYPE, VIDEO_TYPE } from './constants';
import logger from './logger';

/**
 * Capture zoom for the local camera and screenshare in mobile browsers.
 *
 * Desktop browsers do not zoom the capture: there the local user's view zoom
 * of their own video is shared with the others (see features/zoom-sync), so
 * this module leaves desktop cameras untouched.
 *
 * Same split as the native implementation: the camera's own zoom range is
 * used first ({@code MediaStreamTrack.applyConstraints} with the {@code zoom}
 * constraint, which Chromium and Safari expose for cameras that have one -
 * phone rear cameras, UVC webcams with a zoom control), and whatever remains
 * is a centered crop drawn before encoding. Browsers do not expose telephoto
 * lens selection; a camera with no API zoom simply gets the full software
 * crop. A screenshare never has hardware zoom.
 *
 * The crop factor lives in {@link CaptureZoomState} and is read per frame by
 * whichever effect owns the track: the virtual background effect when it is
 * on, otherwise a {@link CameraZoomEffect} that this module installs only
 * while a crop is needed. Installing an effect renegotiates the
 * peerconnection, so it happens on the 1x boundary and never per pinch move.
 */

/**
 * Removing the crop effect after a return to 1x waits this long, so a pinch
 * passing through 1x does not tear the effect down and set it back up.
 */
const REMOVE_EFFECT_DELAY_MS = 500;

/**
 * The crop effect is installed once the remaining software factor reaches
 * this, and removed (after the delay above) once it is back within
 * {@code CROP_EPSILON} of 1x. The gap keeps a pinch hovering around 1x from
 * renegotiating the peerconnection back and forth; the virtual background
 * effect, which has no install cost, crops from {@code CROP_EPSILON} up.
 */
const INSTALL_EFFECT_THRESHOLD = 1.05;

/**
 * Per video type live state.
 */
const sessions = {
    [VIDEO_TYPE.CAMERA]: _createSession(),
    [VIDEO_TYPE.DESKTOP]: _createSession()
};

/**
 * Creates the mutable bookkeeping for one video type.
 *
 * @private
 * @returns {Object}
 */
function _createSession() {
    return {
        // Requested total zoom.
        target: CAMERA_ZOOM_MIN,

        // The MediaStreamTrack hardware zoom was last applied to, its zoom
        // range and the device value currently applied.
        hwTrack: null,
        hwRange: null,
        hwValue: null,
        hwApplied: null,
        hwFailed: false,
        hwWanted: null,
        hwPromise: null,

        // In-flight effect install / delayed removal.
        installing: null,
        removeTimer: null
    };
}

/**
 * Clamps a requested total zoom to the product range.
 *
 * @param {number} zoom - Requested zoom.
 * @private
 * @returns {number}
 */
function _clamp(zoom) {
    const numeric = Number(zoom);

    return Number.isFinite(numeric)
        ? Math.min(CAMERA_ZOOM_MAX, Math.max(CAMERA_ZOOM_MIN, numeric))
        : CAMERA_ZOOM_MIN;
}

/**
 * Finds the redux track for a local video type, if it is live.
 *
 * @param {Object} state - The redux state.
 * @param {string} videoType - 'camera' or 'desktop'.
 * @private
 * @returns {Object|undefined}
 */
function _findLocalTrack(state, videoType) {
    const tracks = state['features/base/tracks'] || [];

    if (videoType === VIDEO_TYPE.DESKTOP) {
        return getLocalDesktopTrack(tracks);
    }

    return getLocalTracks(tracks).find(
        t => t.mediaType === MEDIA_TYPE.VIDEO && t.videoType === VIDEO_TYPE.CAMERA);
}

/**
 * Returns the MediaStreamTrack the camera actually produces. With an effect
 * installed {@code mhTrack.track} is the effect's canvas track; the camera is
 * on the stream the effect took over.
 *
 * @param {Object} mhTrack - The local MHTrack.
 * @private
 * @returns {MediaStreamTrack|undefined}
 */
function _getSourceTrack(mhTrack) {
    const stream = mhTrack._originalStream || mhTrack.stream;

    return stream?.getVideoTracks?.()[0];
}

/**
 * Reads the camera's zoom range in device units, or null without one.
 *
 * Chromium reports UVC zoom in driver units (a Logitech webcam: 100-500) and
 * phone cameras as ratios (1-8), so {@code min} is treated as 1x and
 * {@code max / min} as the hardware factor; both conventions map that way.
 *
 * @param {MediaStreamTrack} track - The camera track.
 * @private
 * @returns {{ min: number, max: number, step: number }|null}
 */
function _getZoomRange(track) {
    if (!track || typeof track.getCapabilities !== 'function' || track.readyState === 'ended') {
        return null;
    }

    let zoom;

    try {
        zoom = track.getCapabilities().zoom;
    } catch {
        return null;
    }

    if (!zoom || !(zoom.min > 0) || !(zoom.max > zoom.min)) {
        return null;
    }

    return {
        min: zoom.min,
        max: zoom.max,
        step: zoom.step > 0 ? zoom.step : 0
    };
}

/**
 * Hardware zoom factor the camera is at, relative to 1x. While a device write
 * is in flight the value being written counts, so the crop is sized for the
 * hardware zoom that is about to land instead of briefly doubling it (and
 * installing a crop effect that the settled hardware makes redundant).
 *
 * @param {Object} session - The camera session.
 * @private
 * @returns {number}
 */
function _hardwareFactor(session) {
    const { hwPromise, hwRange, hwValue, hwWanted } = session;
    const value = hwPromise && hwWanted !== null ? hwWanted : hwValue;

    if (!hwRange || !(value > 0)) {
        return 1;
    }

    return Math.max(1, value / hwRange.min);
}

/**
 * Asks the camera for as much of the target as its range covers. Calls are
 * coalesced: while one {@code applyConstraints} is in flight the newest
 * target is remembered and applied afterwards, so pinching cannot queue up
 * dozens of slow device writes.
 *
 * @param {Object} session - The camera session.
 * @param {MediaStreamTrack} track - The camera track.
 * @param {Function} onSettled - Called after the device value changed.
 * @private
 * @returns {void}
 */
function _applyHardwareZoom(session, track, onSettled) {
    if (session.hwTrack !== track) {
        // A new capture (unmute, device change) starts from whatever zoom
        // the device reports, not from what the previous track was set to.
        session.hwTrack = track;
        session.hwRange = _getZoomRange(track);
        session.hwFailed = false;
        session.hwApplied = null;
        session.hwValue = session.hwRange ? track.getSettings?.().zoom ?? session.hwRange.min : null;
    }

    const range = session.hwRange;

    if (!range || session.hwFailed) {
        return;
    }

    const factor = Math.min(session.target, range.max / range.min);
    let value = range.min * factor;

    if (range.step > 0) {
        value = range.min + (Math.round((value - range.min) / range.step) * range.step);
    }
    value = Math.min(range.max, Math.max(range.min, value));

    // Already there: neither written to the device nor pending.
    if (value === session.hwApplied
            || (session.hwApplied === null && Math.abs(value - (session.hwValue ?? NaN)) < 1e-6)) {
        return;
    }

    session.hwWanted = value;

    if (session.hwPromise) {
        return;
    }

    session.hwPromise = (async () => {
        try {
            // The device may snap the value (a step grid, its own maximum),
            // so completion is judged on what was requested, not read back.
            while (session.hwTrack === track
                    && session.hwWanted !== null
                    && session.hwWanted !== session.hwApplied) {
                const wanted = session.hwWanted;

                try {
                    await track.applyConstraints({ advanced: [ { zoom: wanted } ] });
                    session.hwApplied = wanted;
                    session.hwValue = track.getSettings?.().zoom ?? wanted;
                } catch (error) {
                    if (session.hwTrack === track) {
                        // Keep the last value the device reported and fall
                        // back to a full software crop for this capture.
                        logger.warn('Camera hardware zoom failed; using software zoom', error);
                        session.hwFailed = true;
                        session.hwValue = track.getSettings?.().zoom ?? range.min;
                    }
                    break;
                }
            }
        } finally {
            session.hwPromise = null;
            onSettled();
        }
    })();
}

/**
 * Whether the virtual background owns (or is about to own) the camera's
 * effect slot. Its effect crops from the shared factor itself.
 *
 * @param {Function} getState - The redux getState function.
 * @param {string} videoType - 'camera' or 'desktop'.
 * @private
 * @returns {boolean}
 */
function _isBackgroundEnabled(getState, videoType) {
    return videoType === VIDEO_TYPE.CAMERA
        && Boolean(getState()['features/virtual-background']?.backgroundEffectEnabled);
}

/**
 * Installs or removes the standalone crop effect so that the current
 * software factor is actually drawn.
 *
 * @param {Function} getState - The redux getState function.
 * @param {string} videoType - 'camera' or 'desktop'.
 * @param {Object} mhTrack - The local MHTrack.
 * @private
 * @returns {void}
 */
function _ensureCropEffect(getState, videoType, mhTrack) {
    const session = sessions[videoType];
    const softwareZoom = getSoftwareZoom(videoType);
    const current = mhTrack._streamEffect;
    const backgroundEnabled = _isBackgroundEnabled(getState, videoType);

    if (mhTrack.disposed) {
        return;
    }

    if (softwareZoom > 1 + CROP_EPSILON) {
        if (session.removeTimer) {
            clearTimeout(session.removeTimer);
            session.removeTimer = null;
        }

        // Any installed effect reads the shared factor itself. While the
        // virtual background is enabled its effect is (about to be) on the
        // track; queuing ours behind it would replace it.
        if (current || backgroundEnabled || session.installing || softwareZoom < INSTALL_EFFECT_THRESHOLD) {
            return;
        }

        const effect = new CameraZoomEffect(videoType);

        session.installing = mhTrack.setEffect(effect)
            .catch(error => logger.warn('Failed to install the capture zoom effect', error))
            .then(() => {
                session.installing = null;

                // setEffect swallows its own failures; the effect took only
                // if the track now holds this instance.
                if (mhTrack._streamEffect !== effect) {
                    logger.warn('Capture zoom effect did not take');
                }
            });

        return;
    }

    if (current instanceof CameraZoomEffect && !session.removeTimer && !session.installing) {
        session.removeTimer = setTimeout(() => {
            session.removeTimer = null;

            if (mhTrack._streamEffect instanceof CameraZoomEffect
                    && getSoftwareZoom(videoType) <= 1 + CROP_EPSILON
                    && !_isBackgroundEnabled(getState, videoType)) {
                mhTrack.setEffect(undefined)
                    .catch(error => logger.warn('Failed to remove the capture zoom effect', error));
            }
        }, REMOVE_EFFECT_DELAY_MS);
    }
}

/**
 * Brings one local video type to its requested zoom: hardware first, then
 * the crop for the remainder.
 *
 * @param {Function} getState - The redux getState function.
 * @param {string} videoType - 'camera' or 'desktop'.
 * @param {number} zoom - Requested total zoom.
 * @private
 * @returns {void}
 */
function _reconcile(getState, videoType, zoom) {
    const session = sessions[videoType];
    const track = _findLocalTrack(getState(), videoType);
    const mhTrack = track?.mhTrack;

    session.target = _clamp(zoom);

    if (!mhTrack || track.muted || mhTrack.disposed) {
        // Nothing is captured right now; the unmute / track-added hooks
        // re-run this once there is a stream to zoom.
        return;
    }

    const sync = () => {
        // A later reconcile may have moved the target while hardware settled.
        const residual = Math.max(1, session.target / _hardwareFactor(session));

        setSoftwareZoom(videoType, residual);
        _ensureCropEffect(getState, videoType, mhTrack);
    };

    if (videoType === VIDEO_TYPE.CAMERA) {
        const source = _getSourceTrack(mhTrack);

        if (source) {
            _applyHardwareZoom(session, source, sync);
        }
    }

    sync();
}

/**
 * Sets the local camera's total zoom. Prefer dispatching
 * {@code setCameraZoom}; this exists for API parity with the native module.
 *
 * @param {number} zoom - Requested total zoom.
 * @returns {number} The clamped target zoom.
 */
export function applyCameraZoomValue(zoom) {
    const target = _clamp(zoom);
    const store = window.APP?.store;

    if (store && isMobileBrowser()) {
        _reconcile(store.getState, VIDEO_TYPE.CAMERA, target);
    }

    return target;
}

/**
 * Re-applies the stored camera and screenshare zoom to the live tracks.
 * Idempotent; called by the media middleware after zoom changes, track
 * changes, unmute and virtual background toggles.
 *
 * @param {Object} store - The redux store.
 * @returns {void}
 */
export function applyStoredCameraZoom(store) {
    if (!store || !isMobileBrowser()) {
        return;
    }

    const getState = () => store.getState();
    const media = getState()['features/base/media'] || {};

    _reconcile(getState, VIDEO_TYPE.CAMERA, media.video?.cameraZoom ?? CAMERA_ZOOM_MIN);
    _reconcile(getState, VIDEO_TYPE.DESKTOP, media.screenshare?.zoom ?? CAMERA_ZOOM_MIN);
}
